/**
 * On-demand AI summary of an alert's likely cause.
 *
 * The RCA endpoint returns its 24 h cache on a hit but generates a new
 * summary on a miss, sending host metrics and log lines to the alert
 * organisation's default AI model (limit 20 per host per day). There is no
 * read-only cache lookup, so nothing is requested until the admin clicks.
 */

import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Alert, Badge, Button, Spinner, Text } from '@agion/ui'
import { Brain, RotateCcw } from 'lucide-react'
import { ApiError } from '@/core/infra_api_client'
import type { RcaResponse } from '@/core/infra_types'
import { formatRelativeTime } from '@/core/infra_utils'
import { getRca } from './infra_monitoring.service'

interface AlertRcaSectionProps {
  fingerprint: string
  orgId?: string
}

export function AlertRcaSection({ fingerprint, orgId }: AlertRcaSectionProps) {
  // Keyed so a different alert starts idle instead of showing the previous result.
  return <OnDemandRca key={`${fingerprint}:${orgId ?? ''}`} fingerprint={fingerprint} orgId={orgId} />
}

function OnDemandRca({ fingerprint, orgId }: AlertRcaSectionProps) {
  const { t } = useTranslation(['pages'])
  const request = useMutation({
    mutationFn: () => getRca(fingerprint, orgId),
    retry: false,
  })
  const ask = () => request.mutate()

  if (request.isIdle) {
    return (
      <div className="space-y-3">
        <Text size="sm" color="muted">
          {t('pages:monitoring.rca.disclosure')}
        </Text>
        <Button variant="secondary" size="sm" onClick={ask} className="flex items-center gap-2">
          <Brain size={14} aria-hidden="true" />
          {t('pages:monitoring.rca.request')}
        </Button>
      </div>
    )
  }

  if (request.isPending) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Spinner size="sm" />
        {t('pages:monitoring.rca.loading')}
      </div>
    )
  }

  if (request.isError) {
    const failure = describeFailure(request.error)
    return (
      <RcaProblem
        variant="error"
        message={t(`pages:monitoring.rca.${failure.key}`, { message: request.error.message })}
        onRetry={failure.canRetry ? ask : undefined}
      />
    )
  }

  const rca = request.data
  if (rca?.status === 'rate-limited') {
    return <RcaProblem variant="warning" message={t('pages:monitoring.rca.rateLimited')} />
  }
  if (!rca || rca.status !== 'ok' || !rca.bullets?.length) {
    return <RcaProblem variant="warning" message={t('pages:monitoring.rca.unavailable')} onRetry={ask} />
  }
  return <RcaResult rca={rca} />
}

type FailureKey = 'forbidden' | 'badRequest' | 'serviceError'

function describeFailure(error: Error): { key: FailureKey; canRetry: boolean } {
  if (error instanceof ApiError && error.status === 403) return { key: 'forbidden', canRetry: false }
  if (error instanceof ApiError && error.status === 400) return { key: 'badRequest', canRetry: false }
  return { key: 'serviceError', canRetry: true }
}

function RcaProblem({
  variant,
  message,
  onRetry,
}: {
  variant: 'warning' | 'error'
  message: string
  onRetry?: () => void
}) {
  const { t } = useTranslation(['pages'])
  return (
    <Alert variant={variant}>
      <div className="space-y-2">
        <p>{message}</p>
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry} className="flex items-center gap-2">
            <RotateCcw size={14} aria-hidden="true" />
            {t('pages:monitoring.rca.tryAgain')}
          </Button>
        )}
      </div>
    </Alert>
  )
}

function RcaResult({ rca }: { rca: RcaResponse }) {
  const { t } = useTranslation(['pages'])
  const confidenceLabel = rca.confidence
    ? t(`pages:monitoring.hostDetail.rcaConfidenceLevels.${rca.confidence}`, rca.confidence)
    : undefined

  return (
    <div className="rounded-md bg-slate-50 px-4 py-3 dark:bg-slate-900/40">
      {confidenceLabel && (
        <div className="mb-2 flex items-center gap-2 text-xs">
          <Text size="xs" color="muted" className="uppercase tracking-wider">
            {t('pages:monitoring.hostDetail.rcaConfidence', 'Confidence')}
          </Text>
          <Badge
            variant={rca.confidence === 'high' ? 'success' : rca.confidence === 'medium' ? 'warning' : 'default'}
            data-testid="rca-confidence"
          >
            {confidenceLabel}
          </Badge>
        </div>
      )}
      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300" data-testid="rca-bullets">
        {rca.bullets?.map((bullet, i) => (
          <li key={i}>{bullet}</li>
        ))}
      </ul>
      {rca.generatedAt && (
        <Text size="xs" color="subtle" className="mt-3">
          {t('pages:monitoring.hostDetail.rcaGeneratedAt', 'Generated')} {formatRelativeTime(rca.generatedAt)}
        </Text>
      )}
    </div>
  )
}
